import React, { useState } from "react";
import "./Home.css";
import Footer from "./Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import Masonry from "react-masonry-css";
import { CircleArrowRight, CirclePlus, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedResourceItem = ({ children, navigateTo }) => {
	const { currentUser } = useAuth();
	const navigate = useNavigate();

	const handleClick = () => {
		if (!currentUser) {
			// Show message and redirect to login
			alert("Please log in to access resources");
			navigate("/login");
			return;
		}
		navigate(navigateTo);
	};

	return (
		<div onClick={handleClick} style={{ cursor: "pointer" }}>
			{children}
		</div>
	);
};

function Home() {
	const navigate = useNavigate();
	const [modalOpen, setModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState(null);

	const stepDetails = {
		step1: {
			title: "Family Assessment",
			content:
				"We begin with a comprehensive assessment involving the guardians. During this session, the aim is to understand the unique dynamics and history of your family. This step allows me to gather valuable insights into the challenges faced and the overall family environment.",
		},
		step2: {
			title: "Sibling Perspective Assessment",
			content:
				"Following the initial assessment, siblings will have the opportunity to meet with Tori, our experienced counselor. These sessions are designed to provide a safe and supportive space for siblings to share their perspectives and experiences. This step is crucial in understanding their individual needs and concerns.",
		},
		step3: {
			title: "Bridging the Gap",
			content: (
				<>
					<h4>Understanding Diagnoses</h4>
					<p>
						Regular calls to help siblings gain a deeper understanding of the
						diagnoses affecting their family member(s). This educational
						component is essential for fostering empathy and awareness.
					</p>

					<h4>Individualized Mentoring</h4>
					<p>
						Siblings receive personalized mentoring tailored to the specific
						program their sibling is attending. This approach ensures that the
						support provided is relevant and effective.
					</p>

					<h4>Collaborative Therapeutic Meetings</h4>
					<p>
						I will facilitate meetings between the sibling and their family
						member's therapist or treatment team. This collaborative effort is
						aimed at integrating the sibling into the therapeutic process,
						promoting unity, and enhancing the overall treatment experience.
					</p>

					<h4>Experiential Mentoring</h4>
					<p>
						Through firsthand mentoring, siblings will learn how to navigate the
						complexities of being part of an unstable family unit. This guidance
						is provided by Tori, who has extensive experience in this area.
					</p>

					<h4>Online Resources</h4>
					<p>
						Website resources, including access to therapeutic games, a
						comprehensive diagnoses dictionary, and other valuable materials.
						These tools are designed to support siblings in their journey and
						enhance their understanding of mental health and therapeutic
						processes.
					</p>
				</>
			),
		},
	};

	const openModal = (step) => {
		setModalContent(stepDetails[step]);
		setModalOpen(true);
	};

	const modal = (
		<div
			className={`modal-overlay ${modalOpen ? "active" : ""}`}
			onClick={() => setModalOpen(false)}
		>
			<div className="modal-content" onClick={(e) => e.stopPropagation()}>
				<button className="modal-close" onClick={() => setModalOpen(false)}>
					×
				</button>
				<h2>{modalContent?.title}</h2>
				<div className="modal-body">{modalContent?.content}</div>
			</div>
		</div>
	);

	return (
		<div className="main-container">
			<div className="video-background">
				<video
					autoPlay
					loop
					muted
					playsInline
					ref={(el) => {
						if (el) el.playbackRate = 0.75; // Adjust this value to control speed
					}}
				>
					<source src="/images/49a01d274a.mp4" type="video/mp4" />
				</video>
			</div>

			<section className="hero-section" id="home">
				<h1>
					Bridging the Gap Between <span>Siblings</span> and{" "}
					<span>Family Units</span>
				</h1>
				<p className="hero-text">
					Sibling Kinect provides a structured and supportive environment that
					aims to empower siblings, foster understanding, and promote healing
					within the family unit.
				</p>
				<button className="cta-button" onClick={() => navigate("/contact")}>
					Contact Us
					<FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: "10px" }} />
				</button>
			</section>

			<section className="services-section" id="services">
				<div className="services-container">
					<h2 className="services-title">One-on-One Sibling Support</h2>
					<p className="services-subtitle">
						for the sibling that is home, <em>not</em> receiving primary
						treatment
					</p>
					<div className="services-content">
						<div className="services-text">
							<p className="services-intro">
								When there is a family member in a therapeutic treatment
								process, we provide first-hand experience with problem-solving
								within a family unit through mentorship.
							</p>
							<ul className="services-list">
								<li>
									Individual mentorship with a certified life-coach who has
									first-hand experience as the at-home sibling
								</li>
								<li>
									Connection between the clinical team and at-home sibling
								</li>
								<li>
									Access to one-of-a-kind resources intended to provide a
									greater understanding of mental health and diagnoses
								</li>
								<li>
									Easier transition towards the end of therapeutic treatment for
									the family unit to reacclimate
								</li>
								<li>
									Reeducating the entire family on how to resolve past traumas
									and create a new normal within their homes
								</li>
							</ul>
						</div>
						<img src="/images/sibling-support.jpg" alt="Sibling Support" />
					</div>
				</div>
			</section>

			<section className="about-section" id="about">
				<div className="about-content">
					<img
						src="/headshot.png"
						alt="Professional headshot"
						className="about-image"
					/>
					<div className="about-text">
						<h2>About</h2>
						<p>
							Hello! I’m Tori Cayle, and I specialize in bridging the gap
							between families and programs to support the sibling at home while
							their sibling is in treatment or facing challenges.
						</p>
						<p>
							With a{" "}
							<strong>
								Bachelor of Science in Psychology from Colorado State University
							</strong>
							, I'm passionate about helping families reconnect during
							therapeutic care. My personal experience with a brother in
							treatment has given me a deep understanding of the{" "}
							<strong>unique challenges siblings at home face</strong>.
						</p>
						<p>
							Currently, I work as an Intake and Placement Coordinator at{" "}
							<strong>Thrive Consulting</strong>, where I've gained valuable
							insights into therapeutic programs. This role has also highlighted
							a key area needing attention:{" "}
							<strong>dedicated support for siblings</strong>.
						</p>
						<p>
							I'm committed to filling this gap, ensuring that siblings feel
							seen and supported throughout the therapeutic process. With an
							upbeat and calming approach, I offer mentorship to bridge the
							divide between siblings at home and their loved one's journey.
						</p>
					</div>
				</div>
			</section>

			<section className="steps-section" id="steps">
				<h2>Program Steps</h2>
				<div className="steps-content">
					<div className="step">
						<h3>
							<em>Step 1</em> Family Assessment
						</h3>
						<p>{stepDetails.step1.content}</p>
						<button
							className="learn-more-button"
							onClick={() => openModal("step1")}
						>
							Learn More
						</button>
					</div>

					<div className="step">
						<h3>
							<em>Step 2</em> Sibling Perspective Assessment
						</h3>
						<p>{stepDetails.step2.content}</p>
						<button
							className="learn-more-button"
							onClick={() => openModal("step2")}
						>
							Learn More
						</button>
					</div>

					<div className="step">
						<h3>
							<em>Step 3</em> Bridging the Gap – <em>how can I help?</em>
						</h3>
						<ul>
							<li>Understanding Diagnoses</li>
							<li>Individualized Mentoring</li>
							<li>Collaborative Therapeutic Meetings</li>
							<li>Experiential Mentoring</li>
							<li>Online Resources</li>
						</ul>
						<button
							className="learn-more-button"
							onClick={() => openModal("step3")}
						>
							Learn More
						</button>
					</div>
				</div>
			</section>

			<section className="resources-section" id="resources">
				<Masonry
					breakpointCols={{
						default: 4,
						1100: 3,
						700: 2,
						500: 1,
					}}
					className="resources-masonry-grid"
					columnClassName="resources-masonry-grid_column"
				>
					<div
						className="resource-item logo"
						style={{ height: "100px", marginTop: "20px" }}
					>
						<div className="logo-circle">
							<img
								src="/logo192.png"
								alt="Sibling Kinect Logo"
								className="resources-img"
							/>
						</div>
						<h3>Sibling Kinect</h3>
					</div>

					<ProtectedResourceItem navigateTo="/mental-health-game">
						<div
							className="resource-item"
							style={{
								height: "200px",
								backgroundImage: "url('/images/mental-health-game.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">
								Mental Health Awareness Game
							</h3>
							<CirclePlus className="icon" style={{ color: "#d9d2c3" }} />
						</div>
					</ProtectedResourceItem>

					<div className="resource-item" style={{ height: "200px" }}>
						<h3>Videos</h3>
						<span className="resource-item-subtitle-line"></span>
						<p>Stay Tuned</p>
						<PlayCircle className="icon" />
					</div>

					<ProtectedResourceItem navigateTo="/newsletter">
						<div className="resource-item" style={{ height: "120px" }}>
							<h3>Newsletter</h3>
							<span className="resource-item-subtitle-line"></span>
							<p>Stay Tuned</p>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/resources">
						<div
							className="resource-item"
							style={{
								height: "280px",
								backgroundImage: "url('/images/resources-resource.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">Resources</h3>
							<CircleArrowRight className="icon" style={{ color: "#d9d2c3" }} />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/dictionary">
						<div
							className="resource-item"
							style={{
								height: "200px",
								backgroundImage: "url('/images/newpaper.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">Mental Health Dictionary</h3>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/blog">
						<div className="resource-item" style={{ height: "200px" }}>
							<h3>Blogs</h3>
							<span className="resource-item-subtitle-line"></span>
							<p>Stay Tuned</p>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<div
						className="resource-item image"
						style={{
							height: "320px",
							backgroundImage: "url('/images/childrenImage.jpeg')",
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					></div>
				</Masonry>
			</section>

			<section className="kinect-section">
				<div
					style={{
						height: "300px",
						width: "80%",
						backgroundImage: "url('/images/services-home-bg.jpeg')",
						backgroundSize: "cover",
						backgroundPosition: "center",
						borderRadius: "10px",
					}}
				>
					<h1>Let’s bridge the gap and get kinected!</h1>
					<button className="cta-button" onClick={() => navigate("/contact")}>
						Contact Us
						<FontAwesomeIcon
							icon={faArrowRight}
							style={{ marginLeft: "10px" }}
						/>
					</button>
				</div>
			</section>

			<Footer />
			{modalOpen && modal}
		</div>
	);
}

export default Home;
